import React, { useLayoutEffect, useRef, useState } from 'react';
import { ArrowRight, Globe, Map, RefreshCw, CheckCircle2, Home, Sparkles } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SectionWrapper from '../UI/SectionWrapper';
import BookingModal from '../UI/BookingModal';

gsap.registerPlugin(ScrollTrigger);

const services = [
  {
    id: 'remote-healing',
    title: "Remote Land Healing",
    price: "$400",
    icon: (
      <div className="relative">
        <Globe className="w-10 h-10 text-forest-green" strokeWidth={1.5} />
        <Sparkles className="absolute -top-1.5 -right-1.5 w-5 h-5 text-amber-500 animate-pulse" />
      </div>
    ),
    description: "All work is done remotely which is just as effective as on site. I require your address which I can view on satellite imagery to locate ley lines, geopathic stress, EMF disturbance, underground water and more.",
    details: [
      "Includes detailed report within 14 days",
      "Crystal pack posted to you",
      "Clearing of disruptive energies (house included)",
      "Follow-up phone/Zoom call"
    ],
    note: "For onsite visits in Brisbane, please contact me directly."
  },
  {
    id: 'pre-purchase',
    title: "Pre-purchase Property Check",
    price: "$150",
    icon: (
      <div className="relative">
        <Home className="w-10 h-10 text-forest-green" strokeWidth={1.5} />
        <div className="absolute -bottom-1.5 -right-1.5 bg-white rounded-full p-0.5 border border-stone-100">
           <Map className="w-4 h-4 text-amber-600" />
        </div>
      </div>
    ),
    description: "Planning on buying or renting? Find out if it’s a high vibrational property before you commit. I'll let you know what’s happening on the site and inside the house.",
    details: [
      "Vibrational property assessment",
      "Includes an energetic map",
      "Peace of mind before purchasing",
      "No healing work included"
    ],
    note: "Cost is deducted from a full Land Healing if you proceed."
  },
  {
    id: 'follow-up',
    title: "Follow-up Land Healing",
    price: "$200",
    icon: (
      <div className="relative">
        <RefreshCw className="w-10 h-10 text-forest-green" strokeWidth={1.5} />
        <div className="absolute inset-0 bg-green-100/30 rounded-full blur-xl -z-10"></div>
      </div>
    ),
    description: "Perfect for a 'top up' if you feel the need due to construction, illness, accidents, people moving in/out, or after environmental events.",
    details: [
      "Re-assessment of property energy",
      "Clearing new disturbances",
      "Restoring energetic balance",
      "Maintenance for peace of mind"
    ],
    note: null
  }
];

const Services = () => {
  const containerRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('');

  const handleBookNow = (serviceTitle) => {
    setSelectedService(serviceTitle);
    setIsModalOpen(true);
  };

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const cards = gsap.utils.toArray(".service-card");
      
      gsap.fromTo(cards, 
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.2,
          ease: "power2.out",
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top 85%",
          }
        }
      );
    }, containerRef);
    
    return () => ctx.revert();
  }, []);

  return (
    <>
      <SectionWrapper ref={containerRef} id="services" className="bg-gradient-to-b from-off-white to-earthy-sand/20 py-16 lg:py-20 max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-serif text-forest-green mb-3">Invest in Harmony</h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-base">
            Professional services to restore balance to your land and life.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 px-4 items-stretch">
          {services.map((service, index) => (
            <div 
              key={index} 
              className="service-card bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-stone-100 flex flex-col h-full group relative overflow-hidden"
            >
              {/* Decorative Background Blob */}
              <div className="absolute top-0 right-0 w-24 h-24 bg-sage-mist/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2 group-hover:bg-sage-mist/20 transition-colors duration-500 pointer-events-none"></div>

              <div className="flex flex-col items-center text-center mb-5 relative z-10">
                <div className="w-16 h-16 bg-stone-50 rounded-full flex items-center justify-center mb-4 group-hover:scale-105 transition-transform duration-300 border border-stone-100 shadow-inner">
                  {service.icon}
                </div>
                
                <h3 className="text-xl font-serif text-forest-green mb-1">{service.title}</h3>
                <div className="text-2xl font-medium text-amber-600 mb-2 font-serif">{service.price}</div>
              </div>
              
              <div className="flex-grow space-y-4 relative z-10">
                 <p className="text-gray-600 text-sm leading-relaxed">
                  {service.description}
                </p>

                <ul className="space-y-2">
                  {service.details.map((detail, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-forest-green shrink-0 mt-0.5" />
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>

                {service.note && (
                  <div className="bg-amber-50/50 p-2.5 rounded-lg border border-amber-100/50">
                    <p className="text-xs text-amber-800 italic">
                      Note: {service.note}
                    </p>
                  </div>
                )}
              </div>

              <div className="pt-6 mt-4 border-t border-gray-100 relative z-10">
                <button 
                  onClick={() => handleBookNow(service.title)}
                  className="w-full py-2.5 rounded-lg border border-forest-green text-forest-green font-medium hover:bg-forest-green hover:text-white transition-all duration-300 flex items-center justify-center gap-2 group-hover:gap-3 text-sm"
                >
                  Book Now
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </SectionWrapper>

      <BookingModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        selectedService={selectedService}
        services={services}
      />
    </>
  );
};

export default Services;
